import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import {
  addMonths,
  buildMonthGrid,
  formatIso,
  IsoDate,
  isAfterDay,
  isBeforeDay,
  isSameDay,
  isWithinRange,
  monthLabel,
  parseIsoToLocal,
  startOfMonth,
  today,
  UK_WEEKDAY_HEADERS,
} from './dateUtils';

// Internal calendar grid + month header. Used by both <DateDropdown> (single)
// and <DateRangeDropdown> (range). Keyboard accessible — focus drives a
// cursor; Enter selects; arrows move; PageUp/PageDown change month.

export interface CalendarPanelProps {
  // Single-select mode
  selectedIso?: IsoDate | null;
  // Range-select mode (when both provided, panel renders range highlight)
  rangeStartIso?: IsoDate | null;
  rangeEndIso?: IsoDate | null;
  // Min / max bounds (inclusive)
  minIso?: IsoDate;
  maxIso?: IsoDate;
  // What to do when the user picks a day. The parent decides whether this
  // sets a single value, the start of a range, or the end of a range.
  onPick: (iso: IsoDate) => void;
  // Initial month to view (defaults to selected month, then today's month)
  initialMonth?: Date;
  // Optional aria-label for the grid root
  ariaLabel?: string;
  // Hover preview for range pre-selection
  hoverIso?: IsoDate | null;
  onHoverChange?: (iso: IsoDate | null) => void;
}

function pickInitialMonth(
  selectedIso?: IsoDate | null,
  rangeStartIso?: IsoDate | null,
  initialMonth?: Date,
): Date {
  if (initialMonth) return startOfMonth(initialMonth);
  if (selectedIso) return startOfMonth(parseIsoToLocal(selectedIso));
  if (rangeStartIso) return startOfMonth(parseIsoToLocal(rangeStartIso));
  return startOfMonth(today());
}

export default function CalendarPanel({
  selectedIso,
  rangeStartIso,
  rangeEndIso,
  minIso,
  maxIso,
  onPick,
  initialMonth,
  ariaLabel,
  hoverIso,
  onHoverChange,
}: CalendarPanelProps): JSX.Element {
  const [viewMonth, setViewMonth] = useState<Date>(() =>
    pickInitialMonth(selectedIso, rangeStartIso, initialMonth),
  );
  const gridRef = useRef<HTMLDivElement>(null);

  // Cursor for keyboard navigation. Defaults to the selected day, then today.
  const initialCursor = useMemo(() => {
    if (selectedIso) return parseIsoToLocal(selectedIso);
    if (rangeStartIso) return parseIsoToLocal(rangeStartIso);
    return today();
  }, [selectedIso, rangeStartIso]);
  const [cursor, setCursor] = useState<Date>(initialCursor);

  // When the view jumps to a different month, refresh the cursor to land on a
  // visible day (1st of the new month) so keyboard nav stays predictable.
  useEffect(() => {
    if (
      cursor.getFullYear() !== viewMonth.getFullYear() ||
      cursor.getMonth() !== viewMonth.getMonth()
    ) {
      setCursor(startOfMonth(viewMonth));
    }
  }, [viewMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  const cells = useMemo(() => buildMonthGrid(viewMonth, true), [viewMonth]);

  const minDate = minIso ? parseIsoToLocal(minIso) : null;
  const maxDate = maxIso ? parseIsoToLocal(maxIso) : null;
  const rangeStartDate = rangeStartIso ? parseIsoToLocal(rangeStartIso) : null;
  const rangeEndDate = rangeEndIso ? parseIsoToLocal(rangeEndIso) : null;
  const hoverDate = hoverIso ? parseIsoToLocal(hoverIso) : null;

  // Hover preview for range: when start is set but end is not, show the band
  // from start to hovered cell.
  const previewEnd =
    rangeStartDate && !rangeEndDate && hoverDate ? hoverDate : null;

  function isDisabled(d: Date): boolean {
    if (minDate && isBeforeDay(d, minDate)) return true;
    if (maxDate && isAfterDay(d, maxDate)) return true;
    return false;
  }

  function isSelectedSingle(d: Date): boolean {
    if (!selectedIso) return false;
    return isSameDay(d, parseIsoToLocal(selectedIso));
  }

  function isInRangeBand(d: Date): boolean {
    if (rangeStartDate && rangeEndDate) {
      return isWithinRange(d, rangeStartDate, rangeEndDate);
    }
    if (rangeStartDate && previewEnd) {
      const lo = isBeforeDay(rangeStartDate, previewEnd)
        ? rangeStartDate
        : previewEnd;
      const hi = isBeforeDay(rangeStartDate, previewEnd)
        ? previewEnd
        : rangeStartDate;
      return isWithinRange(d, lo, hi);
    }
    return false;
  }

  function isRangeEdge(d: Date): boolean {
    if (rangeStartDate && isSameDay(d, rangeStartDate)) return true;
    if (rangeEndDate && isSameDay(d, rangeEndDate)) return true;
    return false;
  }

  function onCellClick(d: Date) {
    if (isDisabled(d)) return;
    onPick(formatIso(d));
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    let next: Date | null = null;
    switch (e.key) {
      case 'ArrowLeft':
        next = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() - 1);
        break;
      case 'ArrowRight':
        next = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + 1);
        break;
      case 'ArrowUp':
        next = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() - 7);
        break;
      case 'ArrowDown':
        next = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + 7);
        break;
      case 'PageUp':
        next = addMonths(cursor, -1);
        break;
      case 'PageDown':
        next = addMonths(cursor, 1);
        break;
      case 'Home': {
        const day = cursor.getDay();
        const offset = (day + 6) % 7; // back to Monday
        next = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() - offset);
        break;
      }
      case 'End': {
        const day = cursor.getDay();
        const offset = (7 - ((day + 6) % 7)) - 1;
        next = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + offset);
        break;
      }
      case 'Enter':
      case ' ':
        e.preventDefault();
        onCellClick(cursor);
        return;
      default:
        return;
    }
    if (next) {
      e.preventDefault();
      setCursor(next);
      // Move the view to follow the cursor across month boundaries.
      if (
        next.getFullYear() !== viewMonth.getFullYear() ||
        next.getMonth() !== viewMonth.getMonth()
      ) {
        setViewMonth(startOfMonth(next));
      }
    }
  }

  function gotoPrev() {
    setViewMonth(addMonths(viewMonth, -1));
  }
  function gotoNext() {
    setViewMonth(addMonths(viewMonth, 1));
  }

  return (
    <div className="kk-date-panel" role="group" aria-label={ariaLabel}>
      <div className="kk-date-panel__header">
        <button
          type="button"
          className="kk-date-panel__nav"
          onClick={gotoPrev}
          aria-label="Previous month"
        >
          <ChevronLeftIcon width={16} height={16} />
        </button>
        <div className="kk-date-panel__month" aria-live="polite">
          {monthLabel(viewMonth)}
        </div>
        <button
          type="button"
          className="kk-date-panel__nav"
          onClick={gotoNext}
          aria-label="Next month"
        >
          <ChevronRightIcon width={16} height={16} />
        </button>
      </div>

      <div className="kk-date-panel__weekdays" aria-hidden="true">
        {UK_WEEKDAY_HEADERS.map((w) => (
          <span key={w} className="kk-date-panel__weekday">
            {w}
          </span>
        ))}
      </div>

      <div
        className="kk-date-panel__grid"
        role="grid"
        aria-label={ariaLabel ?? 'Calendar'}
        ref={gridRef}
        tabIndex={0}
        onKeyDown={onKeyDown}
      >
        {cells.map((cell) => {
          const disabled = isDisabled(cell.date);
          const selected = isSelectedSingle(cell.date);
          const inRange = isInRangeBand(cell.date);
          const edge = isRangeEdge(cell.date);
          const isCursor = isSameDay(cell.date, cursor);
          const isToday = isSameDay(cell.date, today());
          const cls = [
            'kk-date-panel__cell',
            cell.outside ? 'kk-date-panel__cell--outside' : null,
            disabled ? 'kk-date-panel__cell--disabled' : null,
            selected ? 'kk-date-panel__cell--selected' : null,
            inRange ? 'kk-date-panel__cell--in-range' : null,
            edge ? 'kk-date-panel__cell--edge' : null,
            isCursor ? 'kk-date-panel__cell--cursor' : null,
            isToday ? 'kk-date-panel__cell--today' : null,
          ]
            .filter(Boolean)
            .join(' ');
          return (
            <button
              key={cell.iso}
              type="button"
              role="gridcell"
              className={cls}
              disabled={disabled}
              aria-selected={selected || edge || undefined}
              aria-current={isToday ? 'date' : undefined}
              onClick={() => onCellClick(cell.date)}
              onMouseEnter={() => onHoverChange?.(cell.iso)}
              onFocus={() => onHoverChange?.(cell.iso)}
              tabIndex={-1}
            >
              {cell.day}
            </button>
          );
        })}
      </div>
    </div>
  );
}
